import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DetectionClipPlayer, { type ClipFrame } from './DetectionClipPlayer';
import { childAPI } from '../utils/api';
import type { AdminChildDetectionsParams } from '../types/childImageDisplay';

interface DetectionJourneyModalProps {
  show: boolean;
  onHide: () => void;
  childId: number | null;
  childName: string;
  fromDate: string;
  toDate: string;
  nurseryId: string;
  nurseryTimezone: string;
}

interface DetectionJourneyMeta {
  displayIntervalMinutes: number;
  totalRawDetections: number;
  totalReturned: number;
}

const MAX_RANGE_DAYS = 7;

const parseDateOnly = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
};

const getInclusiveDayCount = (fromDate: string, toDate: string) => {
  const from = parseDateOnly(fromDate);
  const to = parseDateOnly(toDate);
  if (!from || !to) return null;
  return Math.floor((to.getTime() - from.getTime()) / 86400000) + 1;
};

const DetectionJourneyModal: React.FC<DetectionJourneyModalProps> = ({
  show,
  onHide,
  childId,
  childName,
  fromDate,
  toDate,
  nurseryId,
  nurseryTimezone,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'detectionJourneyModal' });
  const [loading, setLoading] = useState(false);
  const [frames, setFrames] = useState<ClipFrame[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<DetectionJourneyMeta | null>(null);

  const buildDetectionParams = useCallback((): AdminChildDetectionsParams | null => {
    const selectedFromDate = fromDate || toDate;
    const selectedToDate = toDate || fromDate;

    if (!selectedFromDate || !selectedToDate) {
      setError(t('dateRequired'));
      return null;
    }

    const dayCount = getInclusiveDayCount(selectedFromDate, selectedToDate);
    if (dayCount === null || dayCount < 1) {
      setError(t('invalidDateRange'));
      return null;
    }

    if (dayCount > MAX_RANGE_DAYS) {
      setError(t('rangeTooLong', { days: MAX_RANGE_DAYS }));
      return null;
    }

    if (selectedFromDate === selectedToDate) {
      return { date: selectedFromDate };
    }

    return { from_date: selectedFromDate, to_date: selectedToDate };
  }, [fromDate, t, toDate]);

  const loadFrames = useCallback(async () => {
    if (!childId) {
      setError(t('childRequired'));
      return;
    }

    setLoading(true);
    setError(null);
    setFrames([]);
    setMeta(null);

    const params = buildDetectionParams();
    if (!params) {
      setLoading(false);
      return;
    }

    try {
      const response = await childAPI.getChildDetections(nurseryId, childId, params);
      if (!response.success) {
        throw new Error(t('failedLoadFrames'));
      }

      setMeta({
        displayIntervalMinutes: response.display_interval_minutes,
        totalRawDetections: response.total_raw_detections,
        totalReturned: response.total_returned,
      });
      setFrames(response.frames
        .slice()
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        .map(frame => ({
          imageUrl: frame.image_url,
          timestamp: frame.timestamp,
          cameraName: frame.camera_name,
        })));
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setError(t('accessDenied'));
      } else if (status === 404) {
        setError(t('childNotFound'));
      } else if (status === 422) {
        setError(err?.response?.data?.message || t('invalidDateRange'));
      } else {
        setError(err?.response?.data?.message || err?.message || t('failedLoadFrames'));
      }
    } finally {
      setLoading(false);
    }
  }, [buildDetectionParams, childId, nurseryId, t]);

  useEffect(() => {
    if (show && childId && nurseryId) {
      loadFrames();
    }
  }, [show, childId, nurseryId, fromDate, toDate, loadFrames]);

  useEffect(() => {
    if (!show) {
      setFrames([]);
      setError(null);
      setLoading(false);
      setMeta(null);
    }
  }, [show]);

  const dateLabel = fromDate && toDate && fromDate !== toDate ? fromDate + ' - ' + toDate : fromDate || toDate;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-person-video text-primary" />
          <span>{t('title')}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Badge bg="dark">{childName}</Badge>
          {dateLabel && <Badge bg="info">{dateLabel}</Badge>}
          {frames[0] && <Badge bg="secondary">{frames[0].cameraName}</Badge>}
        </div>
        {meta && (
          <Alert variant="secondary" className="py-2 small">
            {t('densitySummary', {
              returned: meta.totalReturned,
              raw: meta.totalRawDetections,
              minutes: meta.displayIntervalMinutes,
            })}
          </Alert>
        )}
        <DetectionClipPlayer frames={frames} loading={loading} error={error} timezone={nurseryTimezone} />
      </Modal.Body>
    </Modal>
  );
};

export default DetectionJourneyModal;
